import Errors from 'configs/errors'
import { makeAutoObservable, runInAction } from 'mobx'
import { RootStore } from 'stores'
import { IFilter, Where } from 'types/common'
import { getLoggingInOrganization, getPublicSSOConfigs } from 'API/auth'
import { countOrganizations, getOrganizations, updateOrganizationById } from 'API/organization'
import { IOrganization } from 'interfaces/organization'
import { getValidArray } from 'utils/common'

class OrganizationStore {
  rootStore: RootStore

  // user's organization
  organization: IOrganization | null = null

  organizations: IOrganization[] = []
  numberOfOrganizations: number = 0

  // public SSO configs for login page
  publicSSOConfigs: Array<{
    provider: string
    enabled: boolean
  }> = []

  // SSO-only authentication flag for UI decisions
  ssoOnlyAuth: boolean = false

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)

    this.rootStore = rootStore
  }

  public async getOrganizations(filter: IFilter<IOrganization> = {}): Promise<IOrganization[]> {
    this.organizations = await getOrganizations(filter)
    return this.organizations
  }

  public async countOrganizations(where: Where<IOrganization> = {}): Promise<number> {
    this.numberOfOrganizations = await countOrganizations(where)
    return this.numberOfOrganizations
  }

  public async getOrganizationBySubdomain(subdomain: string) {
    const response = await getLoggingInOrganization(subdomain)
    const { organization, ssoOnlyAuth } = response
    
    if (!organization) {
      throw new Error(Errors.ORG_NOT_FOUND)
    }
    
    runInAction(() => {
      this.organization = organization
      // Store ssoOnlyAuth for UI decisions
      this.ssoOnlyAuth = ssoOnlyAuth
    })
    return this.organization
  }

  public async getPublicSSOConfigsBySubdomain(subdomain: string) {
    try {
      const response = await getPublicSSOConfigs(subdomain)
      runInAction(() => {
        this.publicSSOConfigs = response.ssoConfigs
      })
      return response.ssoConfigs
    } catch (error) {
      console.error('Error fetching public SSO configs:', error)
      runInAction(() => {
        this.publicSSOConfigs = []
      })
      return []
    }
  }

  public async setOrganization(organization: IOrganization) {
    runInAction(() => {
      this.organization = organization
    })
  }

  public async updateOrganization(organizationId: number, data: Partial<IOrganization>): Promise<void> {
    await updateOrganizationById(organizationId, data)
    
    // Update the current organization in the store if it's the same one
    if (this.organization && this.organization.id === organizationId) {
      runInAction(() => {
        this.organization = { ...this.organization!, ...data }
      })
    }
  }
}

export default OrganizationStore
